<script lang="ts" setup>
import type { Category, Items, TopProduct } from '~~/server/api/menu/main'

export interface SubmenuProps {
    subcategories: Items[]
    parentCategory: Category
    hasSubmenu: boolean
    isActive?: boolean
}
const props = withDefaults(defineProps<SubmenuProps>(), {
    isActive: false,
})

const { $t } = useNuxtApp()

// Accessibility: Track submenu state for screen reader announcements (WCAG 4.1.2)
const submenuAnnouncement = ref<string>('')
let lastAnnouncedState = false

watch(() => props.isActive, (isActive) => {
    if (isActive !== lastAnnouncedState) {
        lastAnnouncedState = isActive
        submenuAnnouncement.value = ''
        nextTick(() => {
            if (isActive) {
                const itemCount = props.subcategories.length
                submenuAnnouncement.value = `${$t('Podponuka')} ${props.parentCategory.title} ${$t('otvorená.')} ${itemCount} ${$t('položiek.')} ${$t('Použite šípky pre navigáciu.')}`
            } else {
                submenuAnnouncement.value = `${$t('Podponuka')} ${props.parentCategory.title} ${$t('zatvorená.')}`
            }
        })
    }
})

const activeSubmenu = useState('activeSubmenu')
const menuProducts = useState<{
    products: TopProduct[]
    categorySlug: string
}>('menuProducts')

const isSubmenuActive = ({
    index,
}: {
    index: number
    slug: string
}): boolean => {
    // mark first item as active only on big submenu
    if (!props.hasSubmenu) return false
    // no submenu clicked?
    if (activeSubmenu.value === undefined && index === 0) {
        // set default top product
        if (props.subcategories[0]?.top_products?.length) {
            menuProducts.value = {
                products: props.subcategories[0].top_products,
                categorySlug: props.subcategories[0]?.slug ?? '',
            }
        }
        return true
    }
    return false
}

const submenuHasProducts = computed((): boolean =>
    props.parentCategory?.items?.some((item) => item.products)
)
</script>
<template>
  <div
    class="submenu justify-content-between mt-md-0 overflow-hidden rounded-bottom 6"
    :class="{
      'submenu-narrow': !props.hasSubmenu,
      'submenu-wide': props.hasSubmenu,
    }"
    :inert="!props.isActive"
  >
    <div
      class="submenu-in mb-4 mb-md-0 p-md-3 w-100"
      @click="(e) => e.stopImmediatePropagation()"
    >
      <ul
        role="menu"
        :aria-label="`${$t('Podponuka')} ${props.parentCategory.title}`"
      >
        <LazySubmenuCategory
          v-for="(category, index) in props.subcategories"
          :key="`submenu-category-${index}`"
          :class="[
            {
              'is-default-active': isSubmenuActive({
                index,
                slug: category.slug,
              }),
              'is-active': activeSubmenu === category.slug,
            },
          ]"
          :parent-category="props.parentCategory"
          :category="category"
          :is-active="activeSubmenu === category.slug || isSubmenuActive({ index, slug: category.slug })"
        />

        <LazySubmenuOtherLinks v-if="submenuHasProducts" />
      </ul>

      <LazySubmenuProducts
        v-if="props.hasSubmenu"
        class="d-none d-lg-block"
      />
    </div>
    <!-- Screen reader announcement for submenu state changes (WCAG 4.1.2) -->
    <div
      aria-live="polite"
      aria-atomic="true"
      class="visually-hidden"
    >
      {{ submenuAnnouncement }}
    </div>
  </div>
</template>
<style>
@import './Submenu.vars.scss';
</style>
<style scoped lang="scss">
@import './Submenu.scss';
</style>
